import { ChangeEvent, FormEvent, useState, forwardRef } from 'react';
import styled from 'styled-components';
import isMobilePhone from 'validator/lib/isMobilePhone';
import Constants from '../../../constants/constants';
import Input from '../Input';
import Button from '../Button';

const Card = styled.div(({ theme }) => {
  const { color, shadow } = theme;

  return `
    background-color: ${color.grayscale[1000]};
    padding: 38px 32px;
    min-width: 300px;
    max-width: 500px;
    margin: 0 auto;

    @media screen and (min-width: 500px) {
      border-radius: 16px;
      box-shadow: ${shadow.dialog};
    }
  `;
});

const Heading = styled.h1(({ theme }) => {
  const { type, color } = theme;

  return `
    font-family: ${type.face};
    font-size: ${type.size.larger};
    color: ${color.grayscale[0]};
    margin-bottom: 0.5rem;
  `;
});

const SmallText = styled.p(({ theme }) => {
  const { type } = theme;

  return `
    font-size: ${type.size.small};
    margin: 1rem 0;
  `;
});

const TinyText = styled.p(({ theme }) => {
  const { type } = theme;

  return `
    font-size: ${type.size.tiny};
    font-style: italic;
    margin: 1rem 0;
  `;
});

const ButtonWrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  gap: 1rem;
  margin-top: 1rem;
`;

export interface IOTPSetup {
  submitCallback: (otpCode?: string) => void;
  cancelCallback: () => void;
  error: string | false;
  isChangePage: boolean;
}

const OTPSetup = forwardRef<HTMLInputElement, IOTPSetup>(
  ({ submitCallback, cancelCallback, error, isChangePage }, ref) => {
    const [phone, setPhone] = useState<string>('');
    const [valid, setValid] = useState<boolean>(false);

    const handleSubmit = (e: FormEvent): void => {
      e.preventDefault();
      submitCallback(phone);
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      setPhone(e.currentTarget.value);
      setValid(isMobilePhone(e.currentTarget.value, 'en-US'));
    };

    const handleCancel = () => {
      cancelCallback();
    };

    return (
      <Card>
        <Heading>{isChangePage ? ' Change OTP Phone Number' : 'One-Time Password Setup'}</Heading>

        <SmallText aria-live="polite">
          Enter your{isChangePage && ' new'} phone number in the field below. We will send a {Constants.OTP_CODE_LENGTH}
          -digit code to the number provided.
        </SmallText>

        <form onSubmit={handleSubmit}>
          <Input
            type="tel"
            name="telephone"
            id="tel_input"
            label="Phone Number"
            value={phone}
            inputMode="tel"
            onChange={handleChange}
            required
            errors={error ? error : ''}
            invalid={error ? true : false}
            ref={ref}
          />

          <TinyText>The phone number you provide will only be used for account verification purposes.</TinyText>

          <ButtonWrapper>
            {isChangePage && (
              <Button type="button" onClick={handleCancel} hollow>
                Cancel
              </Button>
            )}

            <Button type="submit" disabled={!valid}>
              Send Code
            </Button>
          </ButtonWrapper>
        </form>
      </Card>
    );
  }
);

export default OTPSetup;
